import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import FilestoreBrowser from "../FilestoreBrowser";
import { FilestoreFile } from "../../api/filestore";
import {
  DEFAULT_CUSTOM_LAYER_COLOR,
  DEFAULT_CUSTOM_LAYER_OPACITY,
} from "../../types/customLayer";
import LayerStyleEditor from "./LayerStyleEditor";

interface UploadCustomLayerModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (
    name: string,
    filestoreFileId: number,
    color: string,
    opacity: number
  ) => Promise<void>;
  isPending: boolean;
}

const UploadCustomLayerModal: React.FC<UploadCustomLayerModalProps> = ({
  open,
  onClose,
  onUpload,
  isPending,
}) => {
  const [layerName, setLayerName] = useState("");
  const [selectedFile, setSelectedFile] = useState<FilestoreFile | null>(null);
  const [filestoreBrowserOpen, setFilestoreBrowserOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [color, setColor] = useState(DEFAULT_CUSTOM_LAYER_COLOR);
  const [opacity, setOpacity] = useState(DEFAULT_CUSTOM_LAYER_OPACITY);

  const handleFileSelect = (file: FilestoreFile) => {
    setSelectedFile(file);
    if (!layerName) {
      setLayerName(file.original_filename.replace(/\.[^/.]+$/, ""));
    }
  };

  const resetState = () => {
    setLayerName("");
    setSelectedFile(null);
    setColor(DEFAULT_CUSTOM_LAYER_COLOR);
    setOpacity(DEFAULT_CUSTOM_LAYER_OPACITY);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!selectedFile || !layerName) return;
    setError(null);
    try {
      await onUpload(layerName, selectedFile.id, color, opacity);
      resetState();
      onClose();
    } catch {
      setError("Upload failed. Please try again.");
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <>
      <FilestoreBrowser
        open={filestoreBrowserOpen}
        onClose={() => setFilestoreBrowserOpen(false)}
        onSelectFile={handleFileSelect}
        acceptedFileTypes=".zip,.kml,.geojson,.json"
      />
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Upload Custom Layer</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Shapefiles must be uploaded as a .zip archive.
            </Typography>
            <TextField
              label="Layer name"
              size="small"
              fullWidth
              value={layerName}
              onChange={(e) => setLayerName(e.target.value)}
            />
            <Button
              variant="outlined"
              onClick={() => setFilestoreBrowserOpen(true)}
              sx={{ textTransform: "none", justifyContent: "flex-start" }}
            >
              {selectedFile ? selectedFile.original_filename : "Browse my files…"}
            </Button>
            <LayerStyleEditor
              color={color}
              opacity={opacity}
              onColorChange={setColor}
              onOpacityChange={setOpacity}
            />
            {error && (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button onClick={handleClose} color="inherit">
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!selectedFile || !layerName || isPending}
              >
                {isPending ? <CircularProgress size={18} /> : "Add Layer"}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UploadCustomLayerModal;
